import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import CaptionGeneratorModal from "@/components/CaptionGeneratorModal";
import { ArrowLeft, Sparkle, DownloadSimple, Share, House, CurrencyEur, Calendar, MapPin, TrendUp, TrendDown, ChatCircleText } from "@phosphor-icons/react";

/**
 * Wrapped — report annuale shareable dell'agente.
 * Backend: GET /api/wrapped/year/{year} (iter 244)
 *
 * UX:
 *  - Card grande con highlights (case vendute, milioni transati, commissioni stim.)
 *  - Bottone "Condividi" → genera PNG (canvas) e usa Web Share API / download.
 *  - Switch anno (selettore se ce ne sono più di uno disponibile).
 */
function fmtEur(n) {
  if (!n) return "€ 0";
  if (n >= 1_000_000) return `€ ${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `€ ${(n / 1_000).toFixed(0)}k`;
  return `€ ${n.toLocaleString("it-IT")}`;
}

function fmtEurFull(n) {
  if (!n) return "€ 0";
  return `€ ${Math.round(n).toLocaleString("it-IT")}`;
}

// ── Canvas share card ────────────────────────────────────────────────
// Design editoriale 1080×1350 (IG portrait) — stile cover di rivista
// di settore: serif gigante, palette dark green Pronto, KPI tipografiche,
// QR del referral in basso a destra per viralità.
async function renderWrappedCard(data, referralLink) {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // 1. Background — gradient dark green con grain pseudo-aleatorio
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "#7C8B6E");
  grad.addColorStop(0.6, "#142826");
  grad.addColorStop(1, "#0A1816");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // 1b. Soft circular highlight in alto a destra (luce calda)
  const glow = ctx.createRadialGradient(W - 100, 80, 0, W - 100, 80, 520);
  glow.addColorStop(0, "rgba(200,166,107,0.32)");
  glow.addColorStop(1, "rgba(200,166,107,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // 1c. Grain texture (sottile rumore per evitare look "AI render")
  ctx.save();
  ctx.globalAlpha = 0.05;
  for (let i = 0; i < 3500; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.8})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1.2, 1.2);
  }
  ctx.restore();

  // 2. Border decorativo sottile
  ctx.strokeStyle = "rgba(200,166,107,0.35)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  // 3. Top label "PRONTO · WRAPPED"
  ctx.fillStyle = "#C8A66B";
  ctx.font = "700 22px Inter, system-ui, sans-serif";
  ctx.letterSpacing = "4px";
  // Manual letter-spacing via stretch
  drawSpacedText(ctx, "PRONTO  ·  WRAPPED", 80, 130, 6);

  // Linea decorativa sotto
  ctx.strokeStyle = "#C8A66B";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 150);
  ctx.lineTo(180, 150);
  ctx.stroke();

  // 3b. Logo agenzia in alto a destra (se l'agente l'ha caricato dal profilo).
  // Il logo viene caricato con crossOrigin=anonymous per evitare canvas-taint
  // (è già servito con CORS dal backend Pronto). Se fallisce o assente,
  // semplicemente non disegnamo nulla — il PRONTO·WRAPPED a sinistra basta.
  const logoUrl = data.branding?.agency_logo_url;
  if (logoUrl) {
    try {
      const logoImg = await loadImageWithCors(resolveLogoUrlAbs(logoUrl));
      if (logoImg) {
        // Sizing: max box 240×100, mantieni aspect ratio, allinea a destra
        const maxW = 240, maxH = 100;
        const ratio = Math.min(maxW / logoImg.width, maxH / logoImg.height);
        const drawW = Math.round(logoImg.width * ratio);
        const drawH = Math.round(logoImg.height * ratio);
        const drawX = W - 80 - drawW;
        const drawY = 100 - drawH / 2;
        ctx.drawImage(logoImg, drawX, drawY, drawW, drawH);
      }
    } catch {
      /* logo opzionale, skip silenzioso */
    }
  }

  // 4. Anno gigante (focal point)
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "italic 900 320px Georgia, 'Times New Roman', serif";
  ctx.fillText(String(data.year), 70, 410);

  // 5. Tagline editoriale (italic serif)
  const agentFirst = (data.branding?.agent_name || "").split(" ")[0] || "";
  const tagline = agentFirst
    ? `Il mio anno, ${agentFirst}.`
    : "Il mio anno con Pronto.";
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = "italic 500 52px Georgia, serif";
  ctx.fillText(tagline, 80, 480);

  // 6. KPI block — 2 colonne, 2 righe per le 4 metriche
  const kpis = [
    { l: "CASE VENDUTE",      v: String(data.units_sold), delta: data.yoy?.units_delta_pct },
    { l: "VOLUME TRANSATO",   v: fmtEur(data.total_volume_eur), delta: data.yoy?.volume_delta_pct },
    { l: "COMMISSIONI (3%)",  v: fmtEur(data.estimated_commissions_eur) },
    { l: "GIORNI MEDI",       v: data.avg_days_on_market != null ? `${data.avg_days_on_market}gg` : "—", delta: data.yoy?.days_delta_pct, deltaInverse: true },
  ];

  const kpiYStart = 600;
  const kpiColW = (W - 160) / 2;
  const rowH = 270;
  kpis.forEach((k, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = 80 + col * kpiColW;
    const y = kpiYStart + row * rowH;
    const cellMaxW = kpiColW - 30; // padding interno colonna

    // Label
    ctx.fillStyle = "#A8B89A";
    ctx.font = "700 20px Inter, sans-serif";
    drawSpacedText(ctx, k.l, x, y, 3);

    // Value — riduci font automaticamente se sborda (caso "€ 12.500.000")
    ctx.fillStyle = "#FFFFFF";
    let valueFontSize = 95;
    ctx.font = `700 ${valueFontSize}px Inter, system-ui, sans-serif`;
    while (ctx.measureText(k.v).width > cellMaxW && valueFontSize > 48) {
      valueFontSize -= 4;
      ctx.font = `700 ${valueFontSize}px Inter, system-ui, sans-serif`;
    }
    ctx.fillText(k.v, x, y + 100);

    // YoY delta (se presente) — clampato a larghezza colonna
    if (k.delta != null && Number.isFinite(k.delta)) {
      const positive = k.deltaInverse ? k.delta < 0 : k.delta > 0;
      const arrow = k.delta > 0 ? "▲" : k.delta < 0 ? "▼" : "·";
      ctx.fillStyle = positive ? "#A8B89A" : "#E8A07E";
      ctx.font = "600 24px Inter, sans-serif";
      const sign = k.delta > 0 ? "+" : "";
      fillTextClamped(ctx, `${arrow}  ${sign}${k.delta.toFixed(1)}%  vs  ${data.yoy.previous_year}`, x, y + 140, cellMaxW);
    }
  });

  // 7. Bottom: agency line (sinistra) + QR (destra), tutto contenuto entro H-60
  // Ancoraggio: il blocco è alto ~210px e termina a ~H-60 per lasciare aria.
  const bottomBlockTop = H - 270;   // 1080
  const sepY           = bottomBlockTop;
  const qrSize         = 140;
  const qrX            = W - qrSize - 80;        // 860
  const qrY            = bottomBlockTop + 24;    // 1104
  // Label sotto il QR su DUE righe (più leggibile in piccolo, non sbordata)
  const qrLabelY1      = qrY + qrSize + 24;      // 1268
  const qrLabelY2      = qrLabelY1 + 22;         // 1290
  // Colonna sinistra: agency / agente / città / watermark sito
  const leftX          = 80;
  const agencyY        = bottomBlockTop + 52;
  const subY           = bottomBlockTop + 86;
  const watermarkY     = bottomBlockTop + 152;

  // Linea separatrice sopra il blocco
  ctx.strokeStyle = "rgba(200,166,107,0.4)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, sepY);
  ctx.lineTo(W - 80, sepY);
  ctx.stroke();

  // Agency / Nome agente — clamp larghezza per non finire sotto al QR
  const agency = data.branding?.agency_name;
  const agentName = data.branding?.agent_name;
  const city = data.branding?.city;
  const leftLabel = agency || agentName || "Pronto · Agente Immobiliare";
  const leftMaxW = qrX - leftX - 40; // 700px ca.
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "600 28px Inter, sans-serif";
  fillTextClamped(ctx, leftLabel, leftX, agencyY, leftMaxW);
  if (city || (agency && agentName)) {
    ctx.fillStyle = "rgba(168,184,154,0.85)";
    ctx.font = "500 20px Inter, sans-serif";
    const subLabel = agency && agentName ? `${agentName}${city ? ` · ${city}` : ""}` : city;
    fillTextClamped(ctx, subLabel, leftX, subY, leftMaxW);
  }

  // Watermark sito
  ctx.fillStyle = "#A8B89A";
  ctx.font = "500 20px Inter, sans-serif";
  drawSpacedText(ctx, "PRONTOIMMO.IT", leftX, watermarkY, 3);

  // QR del referral link (chi scansiona → iscrizione con tuo codice → 60gg Pro a te)
  if (referralLink) {
    try {
      const QRCode = (await import("qrcode")).default;
      const qrDataUrl = await QRCode.toDataURL(referralLink, {
        margin: 1,
        color: { dark: "#6A7860", light: "#A8B89A" },
        width: 160,
      });
      const qrImg = new Image();
      await new Promise((resolve) => { qrImg.onload = resolve; qrImg.src = qrDataUrl; });
      // Cornice oro
      ctx.fillStyle = "#C8A66B";
      ctx.fillRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12);
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // Label sotto il QR su 2 righe → mai sbordata
      ctx.fillStyle = "#C8A66B";
      ctx.font = "700 13px Inter, sans-serif";
      const line1 = "SCANSIONA";
      const w1 = measureSpacedText(ctx, line1, 2);
      drawSpacedText(ctx, line1, qrX + (qrSize - w1) / 2, qrLabelY1, 2);

      ctx.font = "600 12px Inter, sans-serif";
      const line2 = "100 CREDITI";
      const w2 = measureSpacedText(ctx, line2, 1.6);
      drawSpacedText(ctx, line2, qrX + (qrSize - w2) / 2, qrLabelY2, 1.6);
    } catch (err) {
      console.warn("QR generation failed", err);
    }
  }

  return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}

// Helper: disegna testo con letter-spacing manuale (Canvas non supporta nativamente)
function drawSpacedText(ctx, text, x, y, spacing) {
  const chars = String(text).split("");
  let cx = x;
  for (const ch of chars) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
}

// Helper: misura la larghezza totale di un testo con letter-spacing
function measureSpacedText(ctx, text, spacing) {
  const chars = String(text).split("");
  let total = 0;
  for (const ch of chars) {
    total += ctx.measureText(ch).width + spacing;
  }
  return Math.max(0, total - spacing);
}

// Helper: disegna testo clampato a maxWidth — se sborda, tronca con ellissi.
function fillTextClamped(ctx, text, x, y, maxWidth) {
  const str = String(text || "");
  if (ctx.measureText(str).width <= maxWidth) {
    ctx.fillText(str, x, y);
    return;
  }
  let lo = 0, hi = str.length, best = "";
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const trial = str.slice(0, mid).trimEnd() + "…";
    if (ctx.measureText(trial).width <= maxWidth) {
      best = trial;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  ctx.fillText(best || "…", x, y);
}

// Helper: carica un'immagine remota con CORS abilitato. Restituisce HTMLImageElement
// pronto da usare in ctx.drawImage. Rifiuta dopo 6s o se l'URL fallisce.
function loadImageWithCors(url) {
  return new Promise((resolve, reject) => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => reject(new Error("logo load timeout")), 6000);
    img.onload = () => { clearTimeout(timer); resolve(img); };
    img.onerror = () => { clearTimeout(timer); reject(new Error("logo load error")); };
    img.src = url;
  });
}

// Helper: risolve URL relativo del logo (/api/public/agency/.../logo) → URL assoluto
// usando REACT_APP_BACKEND_URL. Necessario perché la pagina può essere su un
// dominio diverso (prontoimmo.it) rispetto al backend.
function resolveLogoUrlAbs(logoUrl) {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("http://") || logoUrl.startsWith("https://") || logoUrl.startsWith("data:")) return logoUrl;
  const api = process.env.REACT_APP_BACKEND_URL || "";
  if (logoUrl.startsWith("/api/")) return `${api}${logoUrl}`;
  return logoUrl;
}

export default function Wrapped() {
  const navigate = useNavigate();
  const { year: yearParam } = useParams();
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(() => yearParam ? parseInt(yearParam, 10) : new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralLink, setReferralLink] = useState("");
  const [generating, setGenerating] = useState(false);
  const [captionsOpen, setCaptionsOpen] = useState(false);
  const cardPreviewRef = useRef(null);

  useEffect(() => {
    api.get("/wrapped/availability").then(({ data }) => {
      const y = data?.available_years || [];
      setYears(y);
      if (!yearParam && y.length > 0) setYear(y[0]);
    }).catch(() => {});
    // Pre-fetch referral link to embed as QR on the share card (viralità)
    api.get("/referrals/me").then(({ data }) => setReferralLink(data?.link || "")).catch(() => {});
  }, [yearParam]);

  useEffect(() => {
    setLoading(true);
    api.get(`/wrapped/year/${year}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year]);

  const generateShareImage = async () => {
    if (!data || !data.units_sold) {
      toast.error("Nessuna vendita registrata quest'anno");
      return;
    }
    setGenerating(true);
    try {
      const blob = await renderWrappedCard(data, referralLink);
      if (!blob) {
        toast.error("Generazione immagine fallita");
        return;
      }
      const url = URL.createObjectURL(blob);
      try {
        const file = new File([blob], `pronto-wrapped-${data.year}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `Il mio ${data.year} con Pronto`,
            text: `Ho chiuso ${data.units_sold} ${data.units_sold === 1 ? "vendita" : "vendite"} nel ${data.year}. Pronto immobiliare ⚡`,
          });
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = `pronto-wrapped-${data.year}.png`;
          a.click();
          toast.success("Card scaricata · pronta per Instagram");
        }
      } catch {
        /* user cancelled — ignore */
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AppShell title="Wrapped" breadcrumb="Wrapped">
      <div className="mb-6">
        <button onClick={() => navigate("/dashboard")} className="text-sm text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1.5 mb-3" data-testid="wrapped-back">
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </button>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C8A66B] mb-1">Pronto Wrapped</div>
            <h1 className="text-3xl font-semibold text-[#1C1C1A] tracking-tight" data-testid="wrapped-title">
              Il tuo {year} con Pronto.
            </h1>
            <p className="text-[13px] text-[#6B6A65] mt-1.5">Tutto quello che hai chiuso in un anno. Pronto per essere condiviso.</p>
          </div>
          {years.length > 1 && (
            <div className="flex gap-1.5">
              {years.map((y) => (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                    year === y ? "bg-[#7C8B6E] text-white" : "bg-white border border-[#EAE7DF] text-[#3F3D38] hover:border-[#7C8B6E]"
                  }`}
                  data-testid={`wrapped-year-${y}`}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="h-[60vh] bg-[#EAE7DF] vista-pulse rounded-2xl" />
      )}

      {!loading && data && data.units_sold === 0 && (
        <div className="bg-white border border-[#EAE7DF] rounded-2xl p-8 text-center" data-testid="wrapped-empty">
          <div className="inline-flex w-14 h-14 rounded-full bg-[#F1F4ED] items-center justify-center mb-3">
            <Sparkle size={22} weight="duotone" className="text-[#7C8B6E]" />
          </div>
          <h2 className="text-[18px] font-semibold text-[#1C1C1A]">Ancora nessuna vendita per il {year}</h2>
          <p className="text-[13px] text-[#6B6A65] mt-2 max-w-md mx-auto leading-relaxed">
            Il Wrapped si popola quando elimini un annuncio scegliendo <strong className="text-[#7C8B6E]">«Venduto»</strong> + prezzo + data rogito.
            Tra qualche mese, qui ci sarà la tua storia.
          </p>
        </div>
      )}

      {!loading && data && data.units_sold > 0 && (
        <div className="space-y-4" data-testid="wrapped-card">
          {/* Big hero card */}
          <div className="rounded-2xl bg-gradient-to-br from-[#7C8B6E] to-[#6A7860] text-white p-6 sm:p-8 shadow-lg relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#A8B89A]/20 blur-3xl" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A8B89A] mb-2">PRONTO · WRAPPED {data.year}</div>
              <div className="font-editorial text-[80px] sm:text-[110px] leading-none tracking-tighter mb-3" data-testid="wrapped-year-display">{data.year}</div>
              <div className="text-[15px] sm:text-[17px] text-white/85 italic mb-6">Il mio anno con Pronto.</div>

              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <KpiBlock label="Case vendute" value={data.units_sold} icon={House} delta={data.yoy?.units_delta_pct} prevYear={data.yoy?.previous_year} testid="wrapped-kpi-units" />
                <KpiBlock label="Volume transato" value={fmtEur(data.total_volume_eur)} icon={CurrencyEur} delta={data.yoy?.volume_delta_pct} prevYear={data.yoy?.previous_year} testid="wrapped-kpi-volume" />
                <KpiBlock label="Commissioni stim. (3%)" value={fmtEur(data.estimated_commissions_eur)} icon={Sparkle} testid="wrapped-kpi-commissions" />
                {data.avg_days_on_market != null && (
                  <KpiBlock label="Giorni medi di vendita" value={`${data.avg_days_on_market}gg`} icon={Calendar} delta={data.yoy?.days_delta_pct} prevYear={data.yoy?.previous_year} deltaInverse testid="wrapped-kpi-days" />
                )}
              </div>

              {/* YoY badge — appare solo se ho dati dell'anno precedente */}
              {data.yoy && data.yoy.previous_units > 0 && (
                <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C8A66B]/20 border border-[#C8A66B]/40" data-testid="wrapped-yoy-badge">
                  <Sparkle size={12} weight="fill" className="text-[#C8A66B]" />
                  <span className="text-[12px] text-[#FAF7F0] font-medium">
                    {data.yoy.units_delta_pct != null && data.yoy.units_delta_pct >= 0
                      ? `+${data.yoy.units_delta_pct.toFixed(1)}% vendite vs ${data.yoy.previous_year}`
                      : `${data.yoy.units_delta_pct?.toFixed(1)}% vendite vs ${data.yoy.previous_year}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              onClick={generateShareImage}
              disabled={generating}
              className="bg-[#1C1C1A] text-white py-3 px-4 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
              data-testid="wrapped-share-btn"
            >
              <Share size={16} weight="bold" /> {generating ? "Sto generando…" : "Condividi sui social"}
            </button>
            <button
              onClick={generateShareImage}
              disabled={generating}
              className="bg-white border border-[#1C1C1A] text-[#1C1C1A] py-3 px-4 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#F1F4ED] transition-colors disabled:opacity-60"
              data-testid="wrapped-download-btn"
            >
              <DownloadSimple size={16} weight="bold" /> Scarica PNG (IG/Story)
            </button>
          </div>
          {referralLink && (
            <p className="text-[10.5px] text-[#9A968B] text-center -mt-1 leading-snug">
              La card include il QR del tuo link referral. Chi scansiona e si iscrive ti regala <strong className="text-[#C8A66B]">30 crediti</strong>.
            </p>
          )}

          {/* Caption AI — bottone discreto sotto le azioni primary */}
          <button
            onClick={() => setCaptionsOpen(true)}
            className="w-full py-2.5 rounded-full bg-white border border-[#7C8B6E] text-[#7C8B6E] text-[12.5px] font-semibold hover:bg-[#F1F4ED] transition-colors inline-flex items-center justify-center gap-2"
            data-testid="wrapped-captions-btn"
          >
            <ChatCircleText size={14} weight="bold" /> Genera caption Instagram
          </button>

          {/* Additional details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.best_month && (
              <DetailCard label="Mese top" value={data.best_month} sub={`${Math.max(...(data.monthly_distribution || [0]))} vendite`} />
            )}
            {data.discount_vs_asking_pct != null && (
              <DetailCard
                label="Sconto medio vs richiesta"
                value={`${data.discount_vs_asking_pct > 0 ? "-" : "+"}${Math.abs(data.discount_vs_asking_pct)}%`}
                sub="Da prezzo richiesto a prezzo di rogito"
              />
            )}
            {data.avg_price_eur > 0 && (
              <DetailCard label="Prezzo medio venduto" value={fmtEurFull(data.avg_price_eur)} sub="Per immobile" />
            )}
            {data.top_cities && data.top_cities.length > 0 && (
              <DetailCard
                label="Città principale"
                value={data.top_cities[0].city}
                sub={`${data.top_cities[0].count} ${data.top_cities[0].count === 1 ? "vendita" : "vendite"}`}
                icon={MapPin}
              />
            )}
          </div>

          {/* Disclaimer */}
          <p className="text-[10.5px] text-[#9A968B] italic text-center leading-snug px-4 py-2">
            Commissioni stimate al 3% del volume transato (standard di mercato italiano).
            Dati basati sulle vendite che hai registrato quando hai eliminato gli annunci con motivo «Venduto».
          </p>
        </div>
      )}

      <CaptionGeneratorModal
        open={captionsOpen}
        onClose={() => setCaptionsOpen(false)}
        initialContext="wrapped"
        initialDetails={data ? {
          anno: data.year,
          case_vendute: data.units_sold,
          volume_transato: fmtEur(data.total_volume_eur),
          commissioni_stimate: fmtEur(data.estimated_commissions_eur),
          giorni_medi: data.avg_days_on_market != null ? `${data.avg_days_on_market}gg` : null,
          citta_top: data.top_cities?.[0]?.city || null,
        } : {}}
      />
    </AppShell>
  );
}

function KpiBlock({ label, value, icon: Icon, delta, prevYear, deltaInverse, testid }) {
  const hasDelta = delta != null && Number.isFinite(delta);
  const positive = hasDelta && (deltaInverse ? delta < 0 : delta > 0);
  const DeltaIcon = !hasDelta ? null : (delta > 0 ? TrendUp : delta < 0 ? TrendDown : null);
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-[#A8B89A]">
        {Icon && <Icon size={11} weight="bold" />} {label}
      </div>
      <div className="text-[34px] sm:text-[40px] font-semibold leading-tight tabular-nums tracking-tight">
        {value}
      </div>
      {hasDelta && (
        <div className={`inline-flex items-center gap-1 text-[11px] font-medium ${positive ? "text-[#A8B89A]" : "text-[#E8A07E]"}`}>
          {DeltaIcon && <DeltaIcon size={11} weight="bold" />}
          <span>{delta > 0 ? "+" : ""}{delta.toFixed(1)}% vs {prevYear}</span>
        </div>
      )}
    </div>
  );
}

function DetailCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-xl p-4">
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-[#6B6A65] mb-1.5">
        {Icon && <Icon size={11} weight="bold" />} {label}
      </div>
      <div className="text-[20px] font-semibold text-[#1C1C1A] tracking-tight">{value}</div>
      {sub && <div className="text-[11.5px] text-[#9A968B] mt-0.5">{sub}</div>}
    </div>
  );
}
